import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, ChevronDown, Menu, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export default function Header() {
  const location = useLocation();
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const [isMarbleMenuOpen, setIsMarbleMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isMobileHomeOpen, setIsMobileHomeOpen] = useState(false);
  const [isMobileHardwoodOpen, setIsMobileHardwoodOpen] = useState(false);
  const [isMobileInstallationOpen, setIsMobileInstallationOpen] =
    useState(false);
  const [isMobileNaturalStoneOpen, setIsMobileNaturalStoneOpen] =
    useState(false);
  const [isMobileMarbleOpen, setIsMobileMarbleOpen] = useState(false);
  const [isMobileConcreteOpen, setIsMobileConcreteOpen] = useState(false);

  const handleHomeClick = (e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.location.reload();
    }
  };

  const services = [
    {
      name: "Home Remodeling",
      path: "/home-remodeling",
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=200",
      submenu: [
        { name: "Kitchen Remodeling", path: "/kitchen-remodeling" },
        { name: "Bathroom Remodeling", path: "/bathroom-remodeling" },
      ],
    },
    {
      name: "Hardwood Flooring",
      path: "/hardwood-flooring",
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcf602bdcff5b4418bbd0ab40e71ac091?format=webp&width=200",
      submenu: [
        { name: "Refinishing", path: "/hardwood-refinishing" },
        { name: "Buff & Recoat", path: "/hardwood-buff-recoat" },
        { name: "Deep Cleaning", path: "/hardwood-deep-cleaning" },
        { name: "Repair", path: "/hardwood-repair" },
      ],
    },
    {
      name: "Installation",
      path: "/installation",
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F992b60e4958441d2a6b63cc30f4b1dbc?format=webp&width=200",
      submenu: [
        { name: "Stone/Tile", path: "/installation-stone-tile" },
        { name: "Hardwood", path: "/installation-hardwood" },
        { name: "Laminate/Vinyl", path: "/installation-laminate-vinyl" },
        { name: "Stone/Quartz", path: "/installation-stone-quartz" },
      ],
    },
    {
      name: "Natural Stone",
      path: "/natural-stone",
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F913a58de1a874d2a97a3eebb5840971d?format=webp&width=200",
      submenu: [
        { name: "Restoration", path: "/natural-stone-restoration" },
        { name: "Deep Cleaning", path: "/natural-stone-deep-cleaning" },
        { name: "Repair", path: "/natural-stone-repair" },
        { name: "Sealing", path: "/natural-stone-sealing" },
      ],
    },
    {
      name: "Marble Restoration",
      path: "/marble-restoration",
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F913a58de1a874d2a97a3eebb5840971d?format=webp&width=200",
      submenu: [
        { name: "Marble Refinishing", path: "/marble-refinishing" },
        { name: "Marble Polishing", path: "/marble-polishing" },
        { name: "Marble Cleaning & Sealing", path: "/marble-cleaning-sealing" },
        { name: "Marble Repair", path: "/marble-repair" },
        { name: "Marble Stain Removal", path: "/marble-stain-removal" },
        { name: "Marble Etch Removal", path: "/marble-etch-removal" },
      ],
    },
    {
      name: "Concrete Restoration",
      path: "/concrete-restoration",
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F69808c017311416e98257ff39dbfe9ae?format=webp&width=200",
      submenu: [
        {
          name: "Concrete Cleaning & Sealing",
          path: "/concrete-cleaning-sealing",
        },
        {
          name: "Concrete Grinding & Polishing",
          path: "/concrete-grinding-polishing",
        },
        { name: "Concrete Staining", path: "/concrete-staining" },
      ],
    },
  ];

  return (
    <>
      {/* Contact Bar - Not Sticky */}
      <div className="bg-brand-gray-dark text-white py-2">
        <div className="container mx-auto px-4 flex flex-wrap justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a
              href="tel:9495157800"
              className="flex items-center gap-2 hover:text-brand-orange transition"
            >
              <Phone size={16} />
              <span>(949) 515-7800</span>
            </a>
            <a
              href="mailto:info@specializedservicesinc.com"
              className="flex items-center gap-2 hover:text-brand-orange transition"
              aria-label="Email info@specializedservicesinc.com"
            >
              <Mail size={16} />
              <span className="hidden sm:inline">
                info@specializedservicesinc.com
              </span>
            </a>
          </div>
          <div className="text-xs hidden md:block">
            220 Walnut Ave., Huntington Beach, CA 92648
          </div>
        </div>
      </div>

      {/* Main Header - Sticky */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center"
              onClick={handleHomeClick}
            >
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F06cbc622bb674c5999032cd8ae56034e?format=webp&width=800"
                alt="Specialized Services LLC"
                className="h-16 md:h-20"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link
                to="/"
                className="text-brand-gray-dark hover:text-brand-orange transition font-medium"
                onClick={handleHomeClick}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-brand-gray-dark hover:text-brand-orange transition font-medium"
              >
                About
              </Link>

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => {
                  setIsServicesOpen(false);
                  setHoveredService(null);
                }}
              >
                <Link
                  to="/services"
                  className="text-brand-gray-dark hover:text-brand-orange transition font-medium flex items-center gap-1"
                >
                  Services
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
                  />
                </Link>

                {isServicesOpen && (
                  <div
                    className={`absolute top-full left-0 mt-0 bg-white shadow-xl rounded-lg overflow-hidden flex transition-all duration-300 ${hoveredService ? "min-w-[750px]" : "min-w-[250px]"}`}
                  >
                    <div
                      className={`p-2 transition-all duration-300 ${hoveredService ? (isMarbleMenuOpen ? "w-1/3" : "w-1/2") : "w-full"}`}
                    >
                      {services.map((service) =>
                        service.submenu ? (
                          <div
                            key={service.name}
                            className="relative"
                            onMouseEnter={() => {
                              setHoveredService(service.name);
                              setIsMarbleMenuOpen(true);
                            }}
                          >
                            <Link
                              to={service.path}
                              onClick={() => {
                                setIsServicesOpen(false);
                                setHoveredService(null);
                                setIsMarbleMenuOpen(false);
                              }}
                              className="block px-4 py-3 hover:bg-gray-50 transition rounded whitespace-nowrap"
                            >
                              <span className="text-brand-gray-dark hover:text-brand-orange font-medium flex items-center justify-between">
                                {service.name}
                                <ChevronDown size={16} className="-rotate-90" />
                              </span>
                            </Link>
                          </div>
                        ) : (
                          <Link
                            key={service.path}
                            to={service.path!}
                            className="block px-4 py-3 hover:bg-gray-50 transition rounded whitespace-nowrap"
                            onMouseEnter={() => {
                              setHoveredService(service.name);
                              setIsMarbleMenuOpen(false);
                            }}
                          >
                            <span className="text-brand-gray-dark hover:text-brand-orange font-medium">
                              {service.name}
                            </span>
                          </Link>
                        ),
                      )}
                    </div>
                    {hoveredService && isMarbleMenuOpen && (
                      <div
                        className="w-1/3 p-2 bg-gray-50 border-l border-gray-200"
                        onMouseEnter={() => setIsMarbleMenuOpen(true)}
                        onMouseLeave={() => setIsMarbleMenuOpen(false)}
                      >
                        {services
                          .find((s) => s.name === hoveredService)
                          ?.submenu?.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="block px-4 py-2 hover:bg-white transition rounded text-sm"
                            >
                              <span className="text-brand-gray-dark hover:text-brand-orange">
                                {item.name}
                              </span>
                            </Link>
                          ))}
                      </div>
                    )}
                    {hoveredService && (
                      <div className={isMarbleMenuOpen ? "w-1/3" : "w-1/2"}>
                        <img
                          src={
                            services.find((s) => s.name === hoveredService)
                              ?.image
                          }
                          alt={hoveredService}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Link
                to="/projects"
                className="text-brand-gray-dark hover:text-brand-orange transition font-medium"
              >
                Projects
              </Link>
              <Link
                to="/client-list"
                className="text-brand-gray-dark hover:text-brand-orange transition font-medium"
              >
                Client List
              </Link>
              <Link
                to="/contact"
                className="text-brand-gray-dark hover:text-brand-orange transition font-medium"
              >
                Contact
              </Link>
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden text-brand-gray-dark p-2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              <a
                href="tel:9495157800"
                className="hidden sm:flex bg-brand-gray-dark text-white px-4 py-3 rounded-lg font-semibold hover:bg-brand-gray-dark/90 transition items-center gap-2"
                aria-label="Call (949) 515-7800"
              >
                <Phone size={18} />
                <span className="hidden xl:inline">(949) 515-7800</span>
              </a>
              <Link
                to="/free-estimate"
                className="bg-brand-orange text-white px-4 sm:px-6 py-3 rounded-lg font-semibold hover:brightness-90 transition text-sm sm:text-base"
              >
                Free Estimate
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Fixed Positioning */}
      {isMobileMenuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/20 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="lg:hidden fixed left-0 right-0 bottom-0 top-[88px] bg-white z-40 overflow-y-auto shadow-lg">
            <nav className="container mx-auto px-4 py-6 space-y-4">
              <Link
                to="/"
                className="block text-brand-gray-dark hover:text-brand-orange transition font-medium text-lg py-2"
                onClick={(e) => {
                  handleHomeClick(e);
                  setIsMobileMenuOpen(false);
                }}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="block text-brand-gray-dark hover:text-brand-orange transition font-medium text-lg py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>

              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition font-medium text-lg py-2"
                >
                  Services
                  <ChevronDown
                    size={20}
                    className={`transition-transform ${isMobileServicesOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isMobileServicesOpen && (
                  <div className="pl-4 mt-2 space-y-2">
                    <Link
                      to="/services"
                      className="block text-brand-orange hover:text-brand-orange/90 transition py-2 font-semibold"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      View All Services
                    </Link>

                    <div>
                      <button
                        onClick={() => setIsMobileHomeOpen(!isMobileHomeOpen)}
                        className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition py-2"
                      >
                        Home Remodeling
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${isMobileHomeOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isMobileHomeOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          <Link
                            to="/home-remodeling"
                            className="block text-brand-orange hover:text-brand-orange/90 transition py-1.5 text-sm font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Home Remodeling Overview
                          </Link>
                          <Link
                            to="/kitchen-remodeling"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Kitchen Remodeling
                          </Link>
                          <Link
                            to="/bathroom-remodeling"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Bathroom Remodeling
                          </Link>
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() =>
                          setIsMobileHardwoodOpen(!isMobileHardwoodOpen)
                        }
                        className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition py-2"
                      >
                        Hardwood Flooring
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${isMobileHardwoodOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isMobileHardwoodOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          <Link
                            to="/hardwood-flooring"
                            className="block text-brand-orange hover:text-brand-orange/90 transition py-1.5 text-sm font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Hardwood Flooring Overview
                          </Link>
                          <Link
                            to="/hardwood-refinishing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Refinishing
                          </Link>
                          <Link
                            to="/hardwood-buff-recoat"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Buff & Recoat
                          </Link>
                          <Link
                            to="/hardwood-deep-cleaning"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Deep Cleaning
                          </Link>
                          <Link
                            to="/hardwood-repair"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Repair
                          </Link>
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() =>
                          setIsMobileInstallationOpen(!isMobileInstallationOpen)
                        }
                        className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition py-2"
                      >
                        Installation
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${isMobileInstallationOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isMobileInstallationOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          <Link
                            to="/installation"
                            className="block text-brand-orange hover:text-brand-orange/90 transition py-1.5 text-sm font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Installation Overview
                          </Link>
                          <Link
                            to="/installation-stone-tile"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Stone/Tile
                          </Link>
                          <Link
                            to="/installation-hardwood"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Hardwood
                          </Link>
                          <Link
                            to="/installation-laminate-vinyl"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Laminate/Vinyl
                          </Link>
                          <Link
                            to="/installation-stone-quartz"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Stone/Quartz
                          </Link>
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() =>
                          setIsMobileNaturalStoneOpen(!isMobileNaturalStoneOpen)
                        }
                        className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition py-2"
                      >
                        Natural Stone
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${isMobileNaturalStoneOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isMobileNaturalStoneOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          <Link
                            to="/natural-stone"
                            className="block text-brand-orange hover:text-brand-orange/90 transition py-1.5 text-sm font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Natural Stone Overview
                          </Link>
                          <Link
                            to="/natural-stone-restoration"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Restoration
                          </Link>
                          <Link
                            to="/natural-stone-deep-cleaning"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Deep Cleaning
                          </Link>
                          <Link
                            to="/natural-stone-repair"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Repair
                          </Link>
                          <Link
                            to="/natural-stone-sealing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Sealing
                          </Link>
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() =>
                          setIsMobileMarbleOpen(!isMobileMarbleOpen)
                        }
                        className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition py-2"
                      >
                        Marble Restoration
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${isMobileMarbleOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isMobileMarbleOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          <Link
                            to="/marble-restoration"
                            className="block text-brand-orange hover:text-brand-orange/90 transition py-1.5 text-sm font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Restoration Overview
                          </Link>
                          <Link
                            to="/marble-refinishing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Refinishing
                          </Link>
                          <Link
                            to="/marble-polishing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Polishing
                          </Link>
                          <Link
                            to="/marble-cleaning-sealing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Cleaning & Sealing
                          </Link>
                          <Link
                            to="/marble-repair"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Repair
                          </Link>
                          <Link
                            to="/marble-stain-removal"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Stain Removal
                          </Link>
                          <Link
                            to="/marble-etch-removal"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Marble Etch Removal
                          </Link>
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() =>
                          setIsMobileConcreteOpen(!isMobileConcreteOpen)
                        }
                        className="w-full flex items-center justify-between text-brand-gray-dark hover:text-brand-orange transition py-2"
                      >
                        Concrete Restoration
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${isMobileConcreteOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isMobileConcreteOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          <Link
                            to="/concrete-restoration"
                            className="block text-brand-orange hover:text-brand-orange/90 transition py-1.5 text-sm font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Concrete Restoration Overview
                          </Link>
                          <Link
                            to="/concrete-cleaning-sealing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Concrete Cleaning & Sealing
                          </Link>
                          <Link
                            to="/concrete-grinding-polishing"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Concrete Grinding & Polishing
                          </Link>
                          <Link
                            to="/concrete-staining"
                            className="block text-gray-600 hover:text-brand-orange transition py-1.5 text-sm"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Concrete Staining
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/projects"
                className="block text-brand-gray-dark hover:text-brand-orange transition font-medium text-lg py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Projects
              </Link>
              <Link
                to="/client-list"
                className="block text-brand-gray-dark hover:text-brand-orange transition font-medium text-lg py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Client List
              </Link>
              <Link
                to="/contact"
                className="block text-brand-gray-dark hover:text-brand-orange transition font-medium text-lg py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>

              <div className="pt-4 border-t border-gray-200">
                <a
                  href="tel:9495157800"
                  className="flex items-center gap-2 text-brand-orange font-semibold text-lg py-2"
                >
                  <Phone size={20} />
                  (949) 515-7800
                </a>
              </div>
            </nav>
          </div>
        </>
      )}
    </>
  );
}
